import classNames from 'classnames';
import React, { useRef, useState, useCallback } from 'react';

export type ToolTipPosition = 'top-right' | 'top-left' | 'bottom-left' | 'bottom-right';

export interface CustomTooltipWithCustomTriggerProps {
	trigger: React.ReactNode;
	children?: React.ReactNode;
	content?: string | JSX.Element[];
	className?: string;
	classNameBox?: string;
	toolTipPosition?: ToolTipPosition;
	id?: string;
}

export function CustomTooltipWithCustomTrigger({
	trigger,
	children,
	content,
	className,
	classNameBox,
	toolTipPosition = 'top-right',
	id,
}: CustomTooltipWithCustomTriggerProps) {
	const tooltipRef = useRef<HTMLSpanElement>(null);
	const [adjustedPosition, setAdjustedPosition] = useState<ToolTipPosition>(toolTipPosition);

	const handlePointerDown = (e: React.PointerEvent | React.TouchEvent | React.KeyboardEvent) => {
		e.stopPropagation();
	};

	const handleMouseEnter = useCallback(() => {
		if (!tooltipRef.current) return;

		const tooltip = tooltipRef.current;
		const rect = tooltip.getBoundingClientRect();

		let newPosition = toolTipPosition;

		// Check if off-screen and adjust
		const offTop = rect.top < 0;
		const offBottom = rect.bottom > window.innerHeight;
		const offLeft = rect.left < 0;
		const offRight = rect.right > window.innerWidth;

		// Flip vertical if needed
		if (offTop && newPosition.startsWith('top')) {
			newPosition = newPosition.replace('top', 'bottom') as ToolTipPosition;
		} else if (offBottom && newPosition.startsWith('bottom')) {
			newPosition = newPosition.replace('bottom', 'top') as ToolTipPosition;
		}

		// Flip horizontal if needed
		if (offLeft && newPosition.endsWith('left')) {
			newPosition = newPosition.replace('left', 'right') as ToolTipPosition;
		} else if (offRight && newPosition.endsWith('right')) {
			newPosition = newPosition.replace('right', 'left') as ToolTipPosition;
		}

		if (newPosition !== adjustedPosition) {
			setAdjustedPosition(newPosition);
		}
	}, [toolTipPosition, adjustedPosition]);

	const handleMouseLeave = useCallback(() => {
		// Reset to preferred position when not hovering
		setAdjustedPosition(toolTipPosition);
	}, [toolTipPosition]);

	return (
		<span
			id={id}
			className={classNames('custom-tooltip with-custom-trigger', className)}
			onPointerDown={handlePointerDown}
			// @ts-expect-error
			onClick={handlePointerDown}
			onMouseEnter={handleMouseEnter}
			onMouseLeave={handleMouseLeave}
			onKeyDown={handlePointerDown}
			onTouchStart={handlePointerDown}
		>
			{trigger}
			<span ref={tooltipRef} className={classNames('tooltiptext', classNameBox, adjustedPosition)}>
				{children ?? content}
			</span>
		</span>
	);
}
